import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CognitoAuthProvider,
  CognitoAuthenticator,
  storeTokens,
  decodeJwtPayload,
} from "@vault/cognito-auth";

import { setCredentials } from "./authSlice";
import cognitoConfig from "./cognitoConfig";
import styles from "./login.module.css";

const cognitoTranslations = {
  login: {
    heading: "Smagu, kad užsukai!",
  },
};

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" fill="#4285F4" />
    <path d="M3.2 14.1l7 5.1C12.2 15 17.6 11 24 11c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 14.9 2 7.2 6.8 3.2 14.1z" fill="#EA4335" />
    <path d="M24 46c5.4 0 10.3-1.8 14.1-5l-6.5-5.5C29.5 37.1 26.9 38 24 38c-6 0-11.1-4-12.8-9.5l-7 5.4C7.9 41.1 15.4 46 24 46z" fill="#34A853" />
    <path d="M44.5 20H24v8.5h11.8c-1 3.1-2.9 5.6-5.4 7.3l6.5 5.5C40.8 37.8 46 31.3 46 24c0-1.3-.2-2.7-.5-4z" fill="#FBBC05" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="22" height="22" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle cx="24" cy="24" r="22" fill="#1877F2" />
    <path d="M33.1 31.1l1.1-7H27.5v-4.5c0-1.9.9-3.8 4-3.8h3.1V9.4s-2.8-.5-5.5-.5c-5.6 0-9.3 3.4-9.3 9.6v5.3h-6.2v7h6.2V46.7c1.2.2 2.5.3 3.8.3s2.6-.1 3.8-.3V31.1h5.7z" fill="#FFFFFF" />
  </svg>
);

const averbumTheme = {
  colors: {
    primary: "#f48f29",
    primaryHover: "#cc6c0a",
    text: "#3c5359",
    textSecondary: "#5e787c",
    textTertiary: "#768689",
    border: "#dee1e2",
    borderSecondary: "#a7b0b1",
    borderInput: "#768689",
    background: "#ffffff",
    backgroundSecondary: "#eef0f0",
    link: "#56797f",
    focusBorder: "#f48f29",
    focusRing: "rgba(244, 143, 41, 0.3)",
    error: "#d11104",
    errorBackground: "#ffe0de",
    success: "#8f8545",
    successBackground: "#f5f4eb",
  },
  fonts: {
    family: "'Raleway', sans-serif",
  },
  radii: {
    input: "4px",
    button: "4px",
  },
};

const logo = (
  <div className={styles.logo}>
    <img
      src="/logotype.png"
      alt="Averbum"
      className={styles.logoImage}
      style={{ width: "auto", height: "56px" }}
    />
  </div>
);

const Login = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const fromSearch = location.state?.from?.search || "";
  const authError = location.state?.error;
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  useEffect(() => {
    if (authError === "not_invited") {
      setShowInviteDialog(true);
    }
  }, [authError]);

  const closeInviteDialog = () => {
    setShowInviteDialog(false);
    navigate(location.pathname, { replace: true });
  };

  const handleAuthenticated = useCallback(
    (tokens) => {
      storeTokens(tokens);
      const decoded = decodeJwtPayload(tokens.idToken);
      dispatch(
        setCredentials({
          token: tokens.idToken,
          uid: decoded.sub,
          roles: decoded.rls ? decoded.rls.split("|") : [],
        })
      );
      navigate(`${from}${fromSearch}`, { replace: true });
    },
    [dispatch, navigate, from, fromSearch]
  );

  return (
    <div className={styles.page}>
      {showInviteDialog && (
        <div className={styles.overlay} onClick={closeInviteDialog}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.dialogTitle}>{t("auth.notInvitedTitle")}</h2>
            <p className={styles.dialogQuestion}>{t("auth.notInvitedQuestion")}</p>
            <p className={styles.dialogText}>{t("auth.notInvitedFriend")}</p>
            <p className={styles.dialogOr}>{t("auth.notInvitedOr")}</p>
            <p className={styles.dialogText}>
              {t("auth.notInvitedEmail")}{" "}
              <a href="mailto:noriu@averbum.lt" className={styles.dialogEmail}>noriu@averbum.lt</a>{" "}
              {t("auth.notInvitedHelp")}
            </p>
            <button
              className={styles.dialogButton}
              onClick={closeInviteDialog}
            >
              {t("auth.notInvitedClose")}
            </button>
          </div>
        </div>
      )}
      <div className={styles.card}>
        {authError && authError !== "not_invited" && (
          <div className={styles.errorBanner}>
            {t("auth.authError")}
          </div>
        )}
        <CognitoAuthProvider config={cognitoConfig} theme={averbumTheme} translations={cognitoTranslations}>
          <CognitoAuthenticator
            onAuthenticated={handleAuthenticated}
            oauthProviders={[
              { id: "Google", button: <><GoogleIcon /> <span>{t("auth.signInWithGoogle")}</span></> },
              { id: "Facebook", button: <><FacebookIcon /> <span>{t("auth.signInWithFacebook")}</span></> },
            ]}
            showPasswordLogin={false}
            hideSignUp
            logo={logo}
            slots={{ subtitle: <p className={styles.greetingSubtitle}>{t("auth.greetingSubtitle")}</p> }}
          />
        </CognitoAuthProvider>
      </div>
      <footer className={styles.footer}>
        <p className={styles.copyright}>{t("auth.copyright", { year: new Date().getFullYear() })}</p>
        <p className={styles.consent}>
          {t("auth.consentPrefix")}
          <a href="https://averbum.lt/privatumas/index.html" className={styles.consentLink}>
            {t("auth.consentPrivacy")}
          </a>
          {t("auth.consentAnd")}
          <a href="https://averbum.lt/salygos/index.html" className={styles.consentLink}>
            {t("auth.consentTerms")}
          </a>
          {t("auth.consentAge")}
        </p>
      </footer>
    </div>
  );
};

export default Login;
